import React, { createContext } from "react";

import { CitySearchResultWithCateg, Location } from "./custom-types/location-search";
import { GenericObject } from "./custom-types/generic-object";
import { ModalStateNames } from "./custom-types/modal-state";
import { UserAction, UserActionRequiredResponse } from "./constants/userAction";
import { MembershipStatus } from "./custom-types/user";
import { IAgentDetail } from "./custom-types/common-preloads";

export interface KycVerificationMethodTypes {
    aadharVerificationType: number;
}

export type RestrictedEvents = "citySearchCount" | "transporterSearchCount" | "pagination";

export enum ServiceRequestType {
    GPS = "gps",
    CARGO_INSURANCE = "cargoInsurance",
    VEHICLE_INSURANCE = "vehicleInsurance",
    BUSINESS_LOAN = "businessLoan"
}

export interface IAppContext {
    isCsDashboardUser?: boolean;
    isMobile?: boolean;
    screenHeight?: number;
    screenWidth?: number;
    kycVerificationMethodTypes?: KycVerificationMethodTypes;
    setKycVerificationMethodTypes?: (data: KycVerificationMethodTypes) => void;
    headerClassName?: string;
    updateHeaderClassName?: (className: string | undefined) => void;
    currentLocation?: Location;
    setCurrentLocation?: (loc: Location) => void;
    recentTransporterCitySearches?: CitySearchResultWithCateg[];
    updateRecentTransporterCitySearches?: (city: CitySearchResultWithCateg) => void;
    phoneNum?: string | number;
    setPhoneNum?: (num: string | number) => void;
    name?: string;
    setName?: (name?: string) => void;
    userId?: number | string;
    setUserId?: (userId?: number | string) => void;
    companyId?: number | string;
    setCompanyId?: (companyId?: number | string) => void;
    companyLocation?: string;
    setCompanyLocation?: (companyLocation?: string) => void;
    companyLogo?: string;
    setCompanyLogo?: (logoUrl?: string) => void;
    isPhoneVerified?: boolean;
    setIsPhoneVerified?: (isVerfied: boolean) => void;
    /** False until cookie-backed auth is copied into context (SSR and first client paint stay aligned). */
    isAuthReady?: boolean;
    numOfContactRqsts?: number;
    updateNumOfContactRqsts?: () => void;
    transporterContactsRqstd?: string[];
    updateTransporterContactsRqstd?: (newId: string) => void;
    profilesViewed?: string[];
    updateProfilesViewed?: (newProfileId: string) => void;
    serviceType?: string;
    setServiceType?: (serviceType?: string) => void;
    lang?: number;
    setLang?: (lang?: number) => void;
    kycPopupInitialLoad?: string | number;
    setKycPopupInitialLoad?: (newId?: string | number) => void;
    hostURL?: string | undefined;
    historyLocation?: string;
    setHistoryLocation?: (historyLocation?: string) => void;
    isEmailVerified?: boolean;
    setIsEmailVerified?: (isEmailVerified?: boolean) => void;
    isRecoverEmailVerified?: boolean;
    setIsRecoverEmailVerified?: (isRecoveryEmailVerified?: boolean) => void;
    customerCompanyId?: number;
    setCustomerCompanyId?: (customerCompanyId?: number) => void;
    customerPhone?: string;
    setCustomerPhone?: (customerPhone?: string) => void;
    customerName?: string;
    setCustomerName?: (customerName?: string) => void;
    customerServiceType?: string;
    setCustomerServiceType?: (serviceType?: string) => void;
    customerCompanyLocation?: string;
    setCustomerCompanyLocation?: (customerCompanyLocation?: string) => void;
    csDashboardUserRoles?: string[];
    setCsDashboardUserRoles?: (csDashboardUserRoles?: string[]) => void;
    csDashboardPermission?: string[];
    setCsDashboardPermission?: (csDashboardPermission?: string[]) => void;
    shouldPreventLayoutSwitch?: boolean;
    setShouldPreventLayoutSwitch?: (shouldPreventLayoutSwitch: boolean) => void;
    fromFlutterApp?: string;
    isAadhaarVerified?: boolean;
    isPanVerified?: boolean;
    isGstVerified?: boolean;

    isCustomerAadhaarVerified?: boolean;
    isCustomerPanVerified?: boolean;
    isCustomerGstVerified?: boolean;

    //Mandate KYC context only
    revalidateGetDocsStatus?: () => void;
    showMandateKycPopup?: boolean;
    setShowMandateKycPopup?: (val: boolean) => void;
    showAppRedirectionPopup?: boolean;
    appRedirectionPopupProps?: { title?: string; body?: React.ReactNode };
    setShowAppRedirectionPopup?: (val: boolean, props?: { title?: string; body?: React.ReactNode }) => void;
    showGstVerificationPopup?: boolean;
    setShowGstVerificationPopup?: (val: boolean) => void;
    showUpgradeMembershipPlanPopup?: boolean;
    setShowUpgradeMembershipPlanPopup?: (
        val: boolean,
        targetAction?:
            | UserAction.POST_LOAD
            | UserAction.LOAD_BID
            | UserAction.POST_LORRY
            | UserAction.LORRY_BID
            | "default",
        membershipStatus?:
            | UserActionRequiredResponse.BUY_MEMBERSHIP
            | UserActionRequiredResponse.TOP_UP_MEMBERSHIP
            | UserActionRequiredResponse.UPGRADE_MEMBERSHIP
            | UserActionRequiredResponse.DAILY_MEMBERSHIP_LIMIT_EXHAUSTED
            | UserActionRequiredResponse.MEMBERSHIP_EXPIRED
    ) => void;
    upgradeModalTargetAction?: string;
    upgradeModalMembershipStatus?: string;
    resetMandateKycPop?: () => void;
    hideMandateKycPop?: () => void;
    mandateUserVerificationCallBackFunction?: () => {};
    setMandateUserVerificationCallBackFunction?: (mandateUserVerificationCallBackFunction?: () => void) => void;
    mandateUserVerificationActionTriggered?: boolean;
    setMandateUserVerificationActionTriggered?: (val: boolean) => void;
    mandateUserVerificationActionType?: string;
    setMandateUserVerificationActionType?: (val: string) => void;

    setShowClearDuePopup?: ({ isOpen, dueAmount }: { isOpen: boolean; dueAmount: number }) => void;
    showClearDuePopup?: boolean;
    dueAmount?: number;

    //Experimental context
    modalState?: { [key in ModalStateNames]?: boolean };
    setModalState?: (val: { [key in ModalStateNames]?: boolean }) => void;

    // membership
    membershipRequired?: boolean;
    haveMembership?: boolean;
    membershipStatus?: MembershipStatus;
    membershipPlanName?: string;
    membershipPlanServiceId?: number;
    // coins
    coinsAvailable?: number;
    coinsTotal?: number;
    coinsBlocked?: number;
    coinsExpiringToday?: number;

    // bank page form
    showBankAddPopup?: boolean;
    onSuccessAddBankCallback?: () => void;
    setShowBankAddPopup?: (val: boolean, successCallback?: Function) => void;

    // app version
    userAppVersion?: string;
    setUserAppVersion?: (v?: string) => void;

    //Share modal popup
    showShareModalPopup?: { isVisible: boolean; mktType: "load" | "lorry"; data: any };
    setShowShareModalPopup?: ({
        isVisible,
        mktType,
        data
    }: {
        isVisible: boolean;
        mktType: "load" | "lorry";
        data: any;
    }) => void;
    // material list

    userCompanyInfo?: {
        placeId?: string;
        cityName?: string;
        stateName?: string;
        stateId?: number;
    };
    // global data
    globalData?: GenericObject;
    setGlobalData?: (globalData: GenericObject) => void;

    // searchCount
    eventCounts?: {
        citySearchCount?: number;
        transporterSearchCount?: number;
    };
    updateEventCount?: (name: RestrictedEvents, value?: number) => void;
    checkAndHandleGuestUserRestriction?: (name: RestrictedEvents) => boolean;
    abTestVariations?: any;
    setAbTestVariations?: () => void;
    referralCode?: string;
    setReferralCode?: (referralCode?: string) => void;
    route?: string;
    updateRoute?: (newRoute?: string) => void;
    showAppMandatePopup?: boolean;
    activateAppMandatePopup?: () => void;
    sessionId?: number;
    setSessionId?: (num?: number) => void;
    serviceRequestType?: ServiceRequestType;
    setServiceRequestType?: (type?: ServiceRequestType) => void;
    thirdPartyCompanyName?: string;
    setThirdPartyCompanyName?: (companyName?: string) => void;
    tsoLorrySpecialization?: number[];
    setTsoLorrySpecialization?: (l?: number[]) => void;
    // TSO
    taggedAgent?: IAgentDetail;
    plivoCred?: GenericObject;
    isProductionEnv: boolean;
    isMobileScreen: boolean;
    isLorryNotAllowedPopupOpen: boolean;
    toggleIsLorryNotAllowedPopupOpen: Function;
    joinedOn: string;
}

export const DefaultContext: IAppContext = {
    isMobile: true,
    currentLocation: { status: "notRequested" },
    // isPhoneVerified: false,
    profilesViewed: [],
    isProductionEnv: false,
    isMobileScreen: false,
    // numOfContactRqsts: 0,
    isLorryNotAllowedPopupOpen: false,
    toggleIsLorryNotAllowedPopupOpen: () => {},
    joinedOn: ""
} as IAppContext;

export const AppContext = createContext<IAppContext>(DefaultContext);
